import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import {
  HeartPulse, Database, KeyRound, FolderKanban, AlertTriangle,
  FileText, Workflow as WorkflowIcon, Users, Clock, HardDrive, RefreshCcw,
  Timer, Gauge, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend, ComposedChart
} from 'recharts';

const StatusPill = ({ value }) => {
  const ok = value === 'healthy' || value === 'configured';
  return (
    <span
      className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${
        ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
      }`}
      data-testid={`status-${value}`}
    >
      {value}
    </span>
  );
};

const ServiceRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-500" />
      <span className="text-sm text-slate-700">{label}</span>
    </div>
    <StatusPill value={value} />
  </div>
);

const KpiCard = ({ icon: Icon, label, value, color = '#475569', tone = 'slate' }) => {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200',
    blue: 'bg-blue-50 border-blue-100',
    indigo: 'bg-indigo-50 border-indigo-100',
    amber: 'bg-amber-50 border-amber-100',
    emerald: 'bg-emerald-50 border-emerald-100',
    red: 'bg-red-50 border-red-100'
  };
  return (
    <Card className={`border ${toneMap[tone]}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <p className="text-2xl font-bold mt-2" style={{ color, fontFamily: 'var(--font-heading)' }}>{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </CardContent>
    </Card>
  );
};

const SystemHealth = () => {
  const { getAuthHeaders, user } = useAuth();
  const [health, setHealth] = useState(null);
  const [series, setSeries] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  const isSuperAdmin = (user?.roles || []).includes('SuperAdmin');

  const loadAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const headers = getAuthHeaders();
      const calls = [
        api.getSystemHealth(headers),
        api.getHealthTimeseries(days, headers),
        api.getProcessingPerformance(headers)
      ];
      if (isSuperAdmin) calls.push(api.getTenantBreakdown(headers));
      const results = await Promise.all(calls);
      setHealth(results[0].data);
      setSeries(results[1].data?.series || []);
      setPerf(results[2].data);
      if (isSuperAdmin && results[3]) setTenants(results[3].data?.tenants || []);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'ReasonDMS | System Health';
    loadAll();
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
        Loading system health…
      </div>
    );
  }

  if (!health) return null;

  const services = health.services || {};
  const counts = health.counts || {};
  const aiQ = health.ai_queue || {};
  const storage = health.storage || {};

  return (
    <div data-testid="system-health-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">System Health</h1>
          <p className="text-sm text-slate-500 mt-0.5">Live infrastructure, queue depth, and activity trends</p>
        </div>
        <div className="flex gap-2">
          <select
            data-testid="health-days-select"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            data-testid="health-refresh-btn"
            variant="outline"
            size="sm"
            onClick={() => loadAll()}
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={FileText} label="Documents" value={counts.documents ?? 0} color="#2563eb" tone="blue" />
        <KpiCard icon={WorkflowIcon} label="Workflows" value={counts.workflows ?? 0} color="#7c3aed" tone="indigo" />
        <KpiCard icon={Users} label="Users" value={counts.users ?? 0} color="#0891b2" tone="slate" />
        <KpiCard icon={Clock} label="Active Tasks" value={counts.active_workflow_instances ?? 0} color="#ea580c" tone="amber" />
        <KpiCard icon={AlertTriangle} label="Overdue" value={counts.overdue_instances ?? 0} color="#dc2626" tone={counts.overdue_instances ? 'red' : 'emerald'} />
        <KpiCard icon={HeartPulse} label="AI Pending" value={aiQ.pending ?? 0} color="#0d9488" tone="emerald" />
        <KpiCard icon={AlertTriangle} label="AI Failed" value={aiQ.failed ?? 0} color={aiQ.failed ? '#dc2626' : '#10b981'} tone={aiQ.failed ? 'red' : 'emerald'} />
        <KpiCard icon={HardDrive} label="Errors (24h)" value={health.errors_24h ?? 0} color={health.errors_24h ? '#dc2626' : '#10b981'} tone={health.errors_24h ? 'red' : 'emerald'} />
      </div>

      <Tabs defaultValue="services" className="w-full">
        <TabsList className={`grid ${isSuperAdmin ? 'grid-cols-4 max-w-2xl' : 'grid-cols-3 max-w-xl'}`}>
          <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
          <TabsTrigger value="charts" data-testid="tab-charts">Activity Charts</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="tenants" data-testid="tab-tenants">Tenants</TabsTrigger>}
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Services</CardTitle></CardHeader>
              <CardContent>
                <ServiceRow icon={Database} label="MongoDB" value={services.mongodb || 'unknown'} />
                <ServiceRow icon={FolderKanban} label="ChromaDB" value={services.chromadb || 'unknown'} />
                <ServiceRow icon={KeyRound} label="OpenAI / LLM key" value={services.openai_key || 'unknown'} />
                <ServiceRow icon={HardDrive} label="Object storage (FS)" value={services.storage_fs || 'unknown'} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Storage (Plan Allocation)</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1.5" data-testid="system-health-storage">
                {storage.plan_code && <div className="flex justify-between"><span className="text-slate-600">Plan</span><span className="font-medium">{storage.plan_code}</span></div>}
                {storage.exempt ? (
                  <div className="flex justify-between"><span className="text-slate-600">Allocation</span><span className="font-medium text-emerald-600">Unlimited (exempt)</span></div>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-slate-600">Allocated</span><span className="font-medium" data-testid="storage-allocated-gb">{storage.allocated_gb ?? 0} GB</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Used</span><span className="font-medium" data-testid="storage-used-gb">{storage.used_gb ?? 0} GB</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Free</span><span className="font-medium text-emerald-600">{storage.free_gb ?? 0} GB</span></div>
                    <div className="w-full h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
                      <div
                        className={`h-full transition-all ${(storage.utilization_pct ?? 0) >= 90 ? 'bg-red-500' : (storage.utilization_pct ?? 0) >= 75 ? 'bg-amber-500' : 'bg-blue-500'}`}
                        style={{ width: `${Math.min(100, storage.utilization_pct ?? 0)}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{storage.utilization_pct ?? 0}% of plan storage used</p>
                  </>
                )}
                {health.infra && (
                  <div className="border-t border-slate-100 mt-3 pt-2 text-xs text-slate-400 space-y-1">
                    <p className="font-medium text-slate-500">Platform infrastructure</p>
                    <div className="flex justify-between"><span>Container disk</span><span>{health.infra.disk_used_gb} / {health.infra.disk_total_gb} GB</span></div>
                    <div className="flex justify-between"><span>Uploads bucket</span><span>{health.infra.uploads_mb} MB</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="charts" className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Documents created / day</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 220, minWidth: 320 }} data-testid="chart-documents">
                <ResponsiveContainer>
                  <LineChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="documents" stroke="#2563eb" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Workflow actions vs errors / day</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 220, minWidth: 320 }} data-testid="chart-actions">
                <ResponsiveContainer>
                  <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="actions" fill="#7c3aed" />
                    <Bar dataKey="errors" fill="#dc2626" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KpiCard icon={Timer} label="Avg processing time" value={`${perf?.avg_processing_time_seconds ?? 0}s`} color="#2563eb" tone="blue" />
            <KpiCard icon={Gauge} label="p95 processing time" value={`${perf?.p95_processing_time_seconds ?? 0}s`} color="#7c3aed" tone="indigo" />
            <KpiCard icon={Activity} label="Throughput / hr" value={perf?.throughput_per_hour ?? 0} color="#0d9488" tone="emerald" />
            <KpiCard icon={FileText} label="Processed today" value={perf?.docs_processed_today ?? 0} color="#ea580c" tone="amber" />
            <KpiCard icon={FileText} label="Processed (7d)" value={perf?.docs_processed_7d ?? 0} color="#475569" tone="slate" />
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Daily throughput &amp; avg time (30 days)</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 240, minWidth: 320 }} data-testid="chart-performance">
                <ResponsiveContainer>
                  <ComposedChart data={perf?.daily_throughput_30d || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                    <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} unit="s" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" name="Docs processed" fill="#2563eb" />
                    <Line yAxisId="right" type="monotone" dataKey="avg_time" name="Avg time (s)" stroke="#ea580c" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">By document type</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="performance-by-type-table">
                  <thead>
                    <tr className="text-xs text-slate-500 border-b border-slate-200">
                      <th className="text-left py-2 px-3">Document type</th>
                      <th className="text-right py-2 px-3">Processed</th>
                      <th className="text-right py-2 px-3">Avg time (s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(perf?.by_doc_type || {}).map(([t, v]) => (
                      <tr key={t} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium text-slate-700">{t}</td>
                        <td className="text-right py-2 px-3">{v.count}</td>
                        <td className="text-right py-2 px-3">{v.avg_time}</td>
                      </tr>
                    ))}
                    {Object.keys(perf?.by_doc_type || {}).length === 0 && (
                      <tr><td colSpan={3} className="py-6 text-center text-slate-400">No processing metrics recorded yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="tenants" className="space-y-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Per-tenant breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tenant-table">
                    <thead>
                      <tr className="text-xs text-slate-500 border-b border-slate-200">
                        <th className="text-left py-2 px-3">Tenant</th>
                        <th className="text-right py-2 px-3">Orgs</th>
                        <th className="text-right py-2 px-3">Documents</th>
                        <th className="text-right py-2 px-3">Workflows</th>
                        <th className="text-right py-2 px-3">Users</th>
                        <th className="text-right py-2 px-3">Active tasks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map((t) => (
                        <tr key={t.tenant_id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium text-slate-700">{t.name}</td>
                          <td className="text-right py-2 px-3 text-slate-500">{t.org_count ?? 1}</td>
                          <td className="text-right py-2 px-3">{t.documents}</td>
                          <td className="text-right py-2 px-3">{t.workflows}</td>
                          <td className="text-right py-2 px-3">{t.users}</td>
                          <td className="text-right py-2 px-3">{t.active_instances}</td>
                        </tr>
                      ))}
                      {tenants.length === 0 && (
                        <tr><td colSpan={6} className="py-6 text-center text-slate-400">No tenants</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SystemHealth;
